import { useEffect, useMemo, useState } from 'react';
import {
    asyncGetAccount,
    asyncGetAccountByDocument,
    asyncGetAccountHistorical,
} from '../../../../../services/accounts.service';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Account,
    AccountHistorical,
} from '../../../../../shared/types/modules/account';
import {
    ACCOUNTS,
    ACCOUNTS_DETAIL,
    ACCOUNTS_NEW,
} from '../../../../../routes/paths';
import { calculateDateDifference } from '../../../../../shared/utils/date';

export const useAccountDetail = (showHistorical: boolean) => {
    const navigate = useNavigate();
    const { idAccount } = useParams();
    const [loading, setLoading] = useState(false);
    const [accountOutdated, setAccountOutdated] = useState(false);
    const [account, setAccount] = useState<Account>();
    const [accountHistorical, setAccountHistorical] =
        useState<AccountHistorical[]>();

    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );
    const document = queryParams.get('document')?.trim();
    const skill = queryParams.get('skill')?.trim();
    const callId = queryParams.get('callId')?.trim();
    const channel = queryParams.get('channel')?.trim();

    const isUsingDocument = idAccount?.trim() === 'bp'; // differenciates if request comes from Brigth Pattern

    useEffect(() => {
        if (Boolean(idAccount?.trim()) && idAccount !== 'bp') {
            setLoading(true);
            asyncGetAccount(idAccount.trim())
                .then((account) => {
                    if (account) setAccount(account);
                    else navigate(ACCOUNTS);
                })
                .finally(() => setLoading(false));
        } else if (isUsingDocument) {
            setLoading(true);
            asyncGetAccountByDocument(document ?? '')
                .then((account) => {
                    if (account) {
                        setAccount(account);
                        navigate(
                            `${ACCOUNTS_DETAIL}/${account?.id}?skill=${skill}&callId=${callId}&channel=${channel}`
                        );
                    } else
                        navigate(
                            `${ACCOUNTS_NEW}?document=${
                                document ?? ''
                            }&skill=${skill}&callId=${callId}&channel=${channel}`
                        );
                })
                .finally(() => setLoading(false));
        }
    }, [idAccount, document]);

    useEffect(() => {
        if (!account?.updated_at) return;
        const lastUpdate = new Date(account?.updated_at);
        const currentDate = new Date();
        const days = calculateDateDifference(lastUpdate, currentDate, true);
        const DAYS_BEFORE_ACCOUNT_OUTDATED = 183;
        setAccountOutdated(days > DAYS_BEFORE_ACCOUNT_OUTDATED);
    }, [account]);

    useEffect(() => {
        const idAccountParam = isUsingDocument
            ? account?.id
            : idAccount?.trim();
        if (!!idAccountParam && !!showHistorical) {
            asyncGetAccountHistorical(idAccountParam).then((resp) => {
                if (resp) setAccountHistorical(resp);
                else navigate(ACCOUNTS);
            });
        }
    }, [showHistorical]);

    return {
        loading,
        account,
        accountHistorical,
        skill,
        accountOutdated,
        setAccountOutdated,
    };
};
